import { FormEvent, useRef, MouseEvent } from "react";
import { Redirect, useHistory } from "react-router";
import { useAppDispatch, useAppSelector } from "../../context-store";
import { authActions } from "../../context-store/auth-context";
import AuthRequest from "../../models/authentication/AuthRequest";
import authenticationService from "../../services/authentication-service";
import toasterUtils from "../../utils/toaster-utils";
import SignInLogo from "../../assets/img/content/signin.png";
import CandidateLogo from "../../assets/img/logos/candidate.png";
import CompanyLogo from "../../assets/img/logos/employer.png";
import InterviewerLogo from "../../assets/img/logos/interviewer.png";
import EncryptUtil from '../../utils/encode-utils';
import modalUtils from "../../utils/modal-utils";
import { GeneralPoupConfig, ModalSize } from "../../models/modal-types";
import ForgotPassword from "./ForgotPassword";
import { useForm } from "react-hook-form";
import { TextField, Select, MenuItem, Button, FormControl, InputLabel, FormHelperText } from "@mui/material";
import LoginModel from "../../models/authentication/LoginModel";
import { EMAIL_RULE } from "../../utils/validation-utils";

const Login = () => {
    const history = useHistory();
    const authenticated = useAppSelector(
        (state) => state.authState.authenticated
    );
    const dispatch = useAppDispatch();
    const defaultValues: LoginModel = {
        userName: "",
        password: ""
    };
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        reset
    } = useForm<LoginModel>({
        mode: "onChange",
        defaultValues: defaultValues
    });

    const loginHandler = (formData: LoginModel) => {
        if (!isValid) {
            toasterUtils.showError("Please fill Username and Password");
            return;
        }
        let password: string = EncryptUtil.getEncoded(formData.password);
        let userName: string = formData.userName.trim();
        let request: AuthRequest = {
            userName,
            password
        };
        modalUtils.showLoader();
        authenticationService.authenticate(request).then((res) => {
            modalUtils.closeModal();
            if (res.token == null) {
                toasterUtils.showError("Unable to login due to invalid credentials or User is not Authorized, please contact Administrator");
            } else {
                dispatch(authActions.login(res));
            }
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError("Unable to login due to invalid credentials or User is not Authorized, please contact Administrator");
        });
    };

    const handleClear = () => {
        reset(defaultValues);
    };

    const registerUser = (event: MouseEvent, registerType: string) => {
        event.preventDefault();
        history.push("/register/" + registerType);
    };

    if (authenticated) {
        return <Redirect to="/"></Redirect>;
    }

    const onForgotPassword = (event: MouseEvent) => {
        event.preventDefault();
        const config: GeneralPoupConfig = {
            title: "Forgot Password",
            content: (
                <ForgotPassword
                    onForgotPassword={onForgotPasswordHandler}
                />
            ),
            size: ModalSize.MD
        };
        modalUtils.showPopup(config);
    };

    const onForgotPasswordHandler = (userName: string) => {
        modalUtils.closeModal();
        modalUtils.showLoader();
        authenticationService.forgotPassword(userName).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError("Unable to login due to invalid credentials or User is not Authorized, please contact Administrator");
        });
    }

    return (
        <section className="md">
            <div className="container-body">
                <div className="row">
                    <div className="col-lg-6 mb-4 mb-lg-0 login-hero-image">
                        <img src={SignInLogo} alt="..."></img>
                    </div>
                    <div className="col-lg-6">
                      
                            <div className="contact-form-box">
                                <form className="contact quform" onSubmit={handleSubmit(loginHandler)}>
                                    <div className="quform-elements">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <h2 className="section-title">Sign in into your account</h2>
                                            </div>
                                            
                                            <div className="col-md-12">
                                                <FormControl fullWidth margin="normal" error={!!errors.userName}>
                                                    <TextField label="Your user name here" {...register("userName", EMAIL_RULE)} error={!!errors.userName}
                                                        helperText={errors.userName?.message} autoComplete="off" required sx={{ backgroundColor: "white" }} />
                                                </FormControl>
                                            </div>
                                            <div className="col-md-12">
                                                <FormControl fullWidth margin="normal" error={!!errors.password}>
                                                    <TextField type="password" label="Your password here" {...register("password")} error={!!errors.password}
                                                        helperText={errors.password?.message} autoComplete="off" required sx={{ backgroundColor: "white" }} />
                                                </FormControl>
                                            </div>
                                            <div className="col-md-8 sign-in-button">
                                                <Button variant="contained" color="primary" type="submit" size="large" sx={{ mt: 3 }}>
                                                    Sign In
                                                </Button>
                                                <Button variant="contained" color="inherit" className="m-l-xxs" size="large" type="button"
                                                    onClick={handleClear} sx={{ mt: 3, color: "#898989", backgroundColor: "#d1d1d1" }}>
                                                    Cancel
                                                </Button>
                                            </div>
                                            <div className="col-md-4 forgot-password-link">
                                               <a href="#" onClick={onForgotPassword}>Forgot Password?</a>
                                            </div>
                                            <div className="signup-different-user">
                                                <p className="login-title">Sign up as</p>                    
                                                <div className="row">
                                                    <div className="col-lg-4 col-md-4 signup-different-options">
                                                        <img src={CandidateLogo} alt="" width="40" ></img>
                                                        <h3><a onClick={(e) => { registerUser(e, "Candidate"); }}>Candidate</a></h3>
                                                    </div>
                                                    <div className="col-lg-4 col-md-4 signup-different-options">
                                                        <img src={InterviewerLogo} alt="" width="40" ></img>
                                                        <h3><a onClick={(e) => { registerUser(e, "Interviewer"); }}>Interviewer</a></h3>
                                                    </div>
                                                    <div className="col-lg-4 col-md-4 signup-different-options">
                                                        <img src={CompanyLogo} alt="" width="40" ></img>
                                                        <h3><a onClick={(e) => { registerUser(e, "Company"); }}>Employer</a></h3>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                      
                    </div>
                </div>
            </div>
        </section>
    );
};
export default Login;